import React, { useState } from 'react';
import axios from 'axios';
import { baseApiURL } from "../../baseUrl";

const OnlineCompiler = () => {
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [output, setOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [customInput, setCustomInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setOutput('');

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseApiURL()}/compiler/execute`, {
        language,
        code,
        input: customInput
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setOutput(response.data.output);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while executing the code');
    } finally {
      setIsLoading(false);
    }
  };

  const codeSamples = {
    c: `#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}`,
    java: `public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}`,
    python: `print("Hello, World!")`
  };

  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    setCode(codeSamples[newLanguage]);
  };

  return (
    <div className="w-full space-y-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-cyan-500">
            Online Code Compiler
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm font-medium">Write, compile, and run code in C, Java, and Python</p>
        </div>
        
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="language" className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Language:</label>
              <select 
                id="language" 
                value={language} 
                onChange={handleLanguageChange}
                className="bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="c">C</option>
              </select>
            </div>
            
            <button 
              onClick={() => setCode(codeSamples[language])}
              className="px-3.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white rounded-xl text-xs font-bold transition-colors cursor-pointer border border-slate-200 dark:border-slate-700"
            >
              Reset Code
            </button>
          </div>
          
          <div className="mb-4 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="bg-slate-100 dark:bg-slate-950 p-2.5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400 font-semibold">main.{language === 'python' ? 'py' : language === 'java' ? 'java' : 'c'}</span>
            </div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={`Write your ${language} code here...`}
              rows={16}
              className="w-full bg-slate-950 text-indigo-200 p-4 font-mono text-sm focus:outline-none resize-y leading-relaxed"
            />
          </div>

          {/* Custom Input Section */}
          <div className="mb-6">
            <label htmlFor="custom-input" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
              Input (stdin) - Optional
            </label>
            <textarea
              id="custom-input"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Enter input values here (separate multiple inputs with new lines)..."
              rows={3}
              className="w-full bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white p-3 font-mono text-xs rounded-xl border border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-y"
            />
          </div>
          
          <button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-xl font-bold text-xs text-white transition-all cursor-pointer shadow-md ${
              isLoading 
                ? 'bg-slate-600 cursor-not-allowed' 
                : 'bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 shadow-indigo-600/30'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Running Code...
              </div>
            ) : 'Run Code'}
          </button>
        </div>
        
        {output && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-xl p-6">
            <h3 className="text-sm font-bold mb-3 text-emerald-600 dark:text-emerald-400 flex items-center">
              Output
            </h3>
            <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
              <pre className="whitespace-pre-wrap text-emerald-400 font-mono text-xs">{output}</pre>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-xl p-6">
            <h3 className="text-sm font-bold mb-3 text-rose-600 dark:text-rose-400 flex items-center">
              Error
            </h3>
            <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
              <pre className="whitespace-pre-wrap text-rose-400 font-mono text-xs">{error}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineCompiler;